import * as React from "react";
import { escapeRegExp } from "lodash";
import { Pencil } from "lucide-react";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { Review } from "@/services/bedrock/dtos/Review";
import { QuoteEdit } from "@/services/bedrock/dtos/DesignRenderGroup";
import { hasWrittenReview, sameQuote } from "./quoteText";

/**
 * The quote with its highlighted words marked, matched as the designs' highlight filter matches them
 * (case-insensitive, longest phrase first). Marked neutrally: the design decides the real look.
 */
export const HighlightedQuote: React.FC<{ quote: string; phrases?: string[] | null; className?: string }> = ({
  quote,
  phrases,
  className,
}) => {
  const clean = (phrases ?? [])
    .map(p => p.trim())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  if (!clean.length) return <span className={className}>{quote}</span>;
  const pattern = new RegExp(`(${clean.map(escapeRegExp).join("|")})`, "gi");
  // split with one capture group puts the matches at the odd indexes
  const parts = quote.split(pattern);
  return (
    <span className={className}>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="box-decoration-clone rounded-[3px] bg-amber-100 p-0 font-semibold text-amber-900">
            {part}
          </mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </span>
  );
};

/** The quote a review shows in this post: the post's edit when it has one, else the review's own. */
export function effectiveQuote(review: Review, edit?: QuoteEdit | null): { quote: string; phrases: string[] } {
  if (edit) return { quote: edit.condensedQuote, phrases: edit.condensedHighlightPhrases };
  return { quote: review.condensedQuote || review.content || "", phrases: review.condensedHighlightPhrases ?? [] };
}

/** The quote line of a review card or carousel row, or the rating-only line when there's nothing written. */
export const ReviewQuoteLine: React.FC<{ review: Review; edit?: QuoteEdit | null; onEdit?: () => void }> = ({
  review,
  edit,
  onEdit,
}) => {
  if (!hasWrittenReview(review.content)) {
    return (
      <p className="text-sm text-slate-500" data-testid="rating-only">
        {review.starRating != null
          ? `A ${review.starRating}-star rating with no written review.`
          : "A rating with no written review."}
      </p>
    );
  }
  const { quote, phrases } = effectiveQuote(review, edit);
  const edited =
    !!edit &&
    !sameQuote(
      { quote: edit.condensedQuote, phrases: edit.condensedHighlightPhrases },
      { quote: review.condensedQuote, phrases: review.condensedHighlightPhrases }
    );
  return (
    <p
      className={cn(
        "text-sm text-slate-600",
        onEdit && "-mx-1 cursor-pointer rounded-md px-1 transition-colors hover:bg-slate-50 hover:text-slate-800"
      )}
      data-testid="review-quote"
      // A bigger target than the tile for the same thing. The tile stays the control keyboards and screen
      // readers use, so the quote isn't a second button; a click that ends a text selection doesn't count.
      onClick={
        onEdit &&
        (() => {
          if (!window.getSelection()?.toString().trim()) onEdit();
        })
      }
    >
      “<HighlightedQuote quote={quote} phrases={phrases} />”
      {edited && <span className="text-xs text-slate-500"> · edited</span>}
    </p>
  );
};

/** The tall tile beside a review's quote that opens the quote editor. */
export const EditQuoteTile: React.FC<{ onClick: () => void; disabled?: boolean; className?: string }> = ({
  onClick,
  disabled,
  className,
}) => (
  <Button
    type="button"
    variant="outline"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      // A fixed height, centered beside the quote: stretched to a long quote it became a tall, empty box.
      "h-[68px] min-w-[74px] shrink-0 flex-col gap-1.5 self-center rounded-[10px] border-primary px-2.5 py-2.5 text-[11px] leading-tight font-bold text-primary hover:bg-primary/5 hover:text-primary",
      className
    )}
  >
    <Pencil className="size-[22px]" aria-hidden="true" />
    Edit quote
  </Button>
);

export const isQuoteEditable = (review: Review | undefined) => !!review && hasWrittenReview(review.content);
